import React from 'react';
import moment from 'moment';
import 'moment/locale/fr';
import MessageReactions from '../MessageReactions';
import config from '../../config';

const MessageList = ({
    messages,
    filteredMessages,
    isSearchActive,
    userId,
    isCurrentUser,
    userDetails,
    userColors,
    currentMatchMessage,
    onMessageMenu,
    onEditMessage,
    onDeleteMessage,
    onToggleReaction,
    onDownloadAttachment,
    onPreviewMedia,
    highlightSearchTerm,
    searchQuery
}) => {
    // Utility function to format date separators
    const getDateString = (timestamp) => {
        const messageDate = moment(timestamp);
        const today = moment();
        const yesterday = moment().subtract(1, 'day');

        if (messageDate.isSame(today, 'day')) {
            return "Aujourd'hui";
        } else if (messageDate.isSame(yesterday, 'day')) {
            return "Hier";
        } else if (messageDate.isSame(today, 'year')) {
            return messageDate.format('dddd DD MMMM');
        } else {
            return messageDate.format('dddd DD MMMM YYYY');
        }
    };

    // Function to check if date separator should be shown
    const shouldShowDateSeparator = (currentMessage, previousMessage) => {
        if (!previousMessage) return true;
        return !moment(currentMessage.timestamp).isSame(previousMessage.timestamp, 'day');
    };

    const MessagesSkeleton = () => (
        <ul className="messages-list">
            {[1, 2, 3].map((i) => (
                <li key={i} className={`message-item ${i % 2 === 0 ? 'message-right' : 'message-left'}`}>
                    <div className="message-box">
                        <div className="message-content-wrapper">
                            <div className="skeleton-text" style={{
                                width: `${Math.random() * 200 + 100}px`,
                                height: '16px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                marginBottom: '8px'
                            }}>
                            </div>
                        </div>
                        <div className="message-meta">
                            <div className="skeleton-shimmer" style={{
                                width: '80px',
                                height: '12px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                marginTop: '4px',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div className="shimmer-effect"></div>
                            </div>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );

    const formatAttachmentUrl = (attachmentUrl) => {
        if (attachmentUrl.startsWith('http')) {
            return attachmentUrl;
        }
        if (attachmentUrl.includes('s3.amazonaws.com')) {
            return `https://${attachmentUrl.replace(/^https?:\/\//, '')}`;
        }
        return `${config.backendUrl}${attachmentUrl}`;
    };

    const renderMessageContent = (message) => {
        if (message.audio) {
            return (
                <div className="message-audio">
                    <audio controls>
                        <source src={formatAttachmentUrl(message.audio)} type="audio/webm" />
                        <source src={formatAttachmentUrl(message.audio)} type="audio/mp3" />
                        Votre navigateur ne supporte pas l'élément audio.
                    </audio>
                    {message.audio_duration && (
                        <small className="audio-duration">
                            {Math.floor(message.audio_duration / 60)}:{(message.audio_duration % 60).toString().padStart(2, '0')}
                        </small>
                    )}
                </div>
            );
        }

        if (message.attachments && message.attachments.length > 0) {
            return (
                <div className="message-attachments">
                    {message.attachments.map((attachment, idx) => (
                        <div key={idx} className="message-attachment">
                            {attachment.type === 'image' ? (
                                <img
                                    src={formatAttachmentUrl(attachment.url)}
                                    alt={attachment.name}
                                    className="message-image"
                                    onClick={() => onPreviewMedia(attachment)}
                                />
                            ) : attachment.type === 'video' ? (
                                <video
                                    src={formatAttachmentUrl(attachment.url)}
                                    controls
                                    className="message-video"
                                />
                            ) : (
                                <div className="message-file">
                                    <a
                                        href={formatAttachmentUrl(attachment.url)}
                                        download={attachment.name}
                                        className="file-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onDownloadAttachment(attachment.url, attachment.name);
                                        }}
                                    >
                                        📎 {attachment.name}
                                    </a>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // Highlight search terms in message content
        let content = message.content;
        if (isSearchActive && searchQuery && highlightSearchTerm) {
            content = highlightSearchTerm(message.content, searchQuery);
        }

        return <div dangerouslySetInnerHTML={{ __html: content }} />;
    };

    const messagesArray = isSearchActive ? filteredMessages : messages;

    if (messagesArray.length === 0) {
        return (
            <div className="wzc-messages-container">
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6b7280',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    {isSearchActive ? (
                        <div>
                            <p>No messages found for "{searchQuery}"</p>
                            <p><small>Try adjusting your search terms or filters.</small></p>
                        </div>
                    ) : (
                        <>
                            <p>Aucun message pour le moment.</p>
                            <p><small>Commencez la conversation en envoyant un message ci-dessous.</small></p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (!userId) {
        return (
            <div className="wzc-messages-container">
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6b7280',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <p>Loading messages...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="wzc-messages-container">
            <ul className="wzc-messages-list">
                {messagesArray.map((message, index) => {
                    const isCurrentMatch = currentMatchMessage?.id === message.id;
                    const messageSenderId = message.sender_uuid || message.sender;
                    const isOwnMessage = isCurrentUser(messageSenderId);

                    const previousMessage = index > 0 ? messagesArray[index - 1] : null;
                    const showDateSeparator = shouldShowDateSeparator(message, previousMessage);

                    const senderDetails = userDetails[messageSenderId];

                    return (
                        <React.Fragment key={message.id || message.tempId}>
                            {showDateSeparator && (
                                <div className="date-separator">
                                    <div className="date-separator-line"></div>
                                    <span className="date-separator-text">
                                        {getDateString(message.timestamp)}
                                    </span>
                                    <div className="date-separator-line"></div>
                                </div>
                            )}

                            <li
                                data-message-id={message.id}
                                className={`wzc-message-item ${isOwnMessage ? 'wzc-message-right' : 'wzc-message-left'} ${isCurrentMatch ? 'search-match' : ''}`}
                            >
                                <div className="wzc-message-box">
                                    {!isOwnMessage && senderDetails && (
                                        <div className="wzc-message-sender">
                                            {senderDetails.profile_picture ? (
                                                <img
                                                    src={senderDetails.profile_picture}
                                                    alt="Sender"
                                                    className="wzc-sender-avatar"
                                                />
                                            ) : (
                                                <div
                                                    className="wzc-sender-initials"
                                                    style={{ backgroundColor: userColors[messageSenderId] || '#003366' }}
                                                >
                                                    {(senderDetails.first_name || senderDetails.username || 'U')[0] || 'U'}
                                                </div>
                                            )}
                                            <span className="wzc-sender-name">
                                                {`${senderDetails.first_name || ''} ${senderDetails.last_name || ''}`.trim() || senderDetails.username || 'Utilisateur'}
                                            </span>
                                        </div>
                                    )}

                                    <div className="wzc-message-content-wrapper">
                                        {renderMessageContent(message)}

                                        <div className="wzc-message-meta">
                                            <span className="wzc-message-time">
                                                {moment(message.timestamp).format('HH:mm')}
                                            </span>
                                            {message.status === 'sending' && (
                                                <span className="wzc-message-status">Envoi en cours...</span>
                                            )}
                                            {message.is_read && isOwnMessage && (
                                                <span className="wzc-message-read">✓✓</span>
                                            )}
                                        </div>
                                    </div>

                                    <MessageReactions
                                        messageId={message.id}
                                        reactions={message.reactions || []}
                                        onToggleReaction={onToggleReaction}
                                    />

                                    <button
                                        className="wzc-message-menu-btn"
                                        onClick={(e) => onMessageMenu(e, message)}
                                    >
                                        ⋮
                                    </button>
                                </div>
                            </li>
                        </React.Fragment>
                    );
                })}
            </ul>
        </div>
    );
};

export default MessageList;